<template>
  <PopUpContents ref="popup">
    <div class="container-fluid p-3 bb-tooltip-wrapper">
      <div class="col p-2">
        <div class="row">
          <div class="bb-hdg mb-2 text-center bb-truncate-text">Inventory</div>
        </div>

        <div class="row mt-3 mb-3">
          <div class="col form-group">
            <ToolTip
              :text="displayTotalEnc"
              position="bottom"
            >
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="formatEnc(totalEnc)"
                  id="inventory_total_enc"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="inventory_total_enc"
                  >Total Enc
                  <span class="bb-orange-circle"></span>
                </label>
              </div>
            </ToolTip>
          </div>
          <div class="col form-group">
            <ToolTip
              :text="displayCurrentEnc"
              position="bottom"
            >
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="formatEnc(currentEnc)"
                  id="inventory_current_enc"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="inventory_current_enc"
                  >Current Enc
                  <span class="bb-orange-circle"></span>
                </label>
              </div>
            </ToolTip>
          </div>
          <div class="col form-group">
            <ToolTip
              :text="displayAvailableEnc"
              position="bottom"
            >
              <div class="form-floating">
                <input
                  readonly
                  :class="[
                    'form-control text-center',
                    { 'bb-enc-overload': availableEnc < 0 },
                  ]"
                  :value="formatEnc(availableEnc)"
                  id="inventory_available_enc"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="inventory_available_enc"
                  >Available Enc
                  <span class="bb-orange-circle"></span>
                </label>
              </div>
            </ToolTip>
          </div>
          <div class="col form-group">
            <ToolTip
              :text="displayDirectItems"
              position="bottom"
            >
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="directItemsCount"
                  id="inventory_direct_items"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="inventory_direct_items"
                  >Direct Items
                  <span class="bb-orange-circle"></span>
                </label>
              </div>
            </ToolTip>
          </div>
        </div>

        <div class="row mt-3 mb-3 align-items-end">
          <div class="col form-group">
            <ToolTip
              :text="displayStoredItems"
              position="bottom"
            >
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="storedItemsCount"
                  id="inventory_stored_items"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="inventory_stored_items"
                  >Stored Items
                  <span class="bb-orange-circle"></span>
                </label>
              </div>
            </ToolTip>
          </div>
          <div class="col form-group">
            <ToolTip
              :text="displayAttachedItems"
              position="bottom"
            >
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="attachedItemsCount"
                  id="inventory_attached_items"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="inventory_attached_items"
                  >Attached Items
                  <span class="bb-orange-circle"></span>
                </label>
              </div>
            </ToolTip>
          </div>
          <div class="col form-group">
            <ToolTip
              :text="displayTotalItems"
              position="bottom"
            >
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  :value="totalItemsCount"
                  id="inventory_total_items"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="inventory_total_items"
                  >Total Items
                  <span class="bb-orange-circle"></span>
                </label>
              </div>
            </ToolTip>
          </div>
          <div class="col form-group">
            <div
              class="btn-group special w-100"
              role="group"
            >
              <button
                type="button"
                @click="_cancel"
                class="btn btn-lg btn-warning w-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <div class="row mt-3 mb-3">
          <div class="col">
            <div
              v-if="inventoryNodes.length === 0"
              class="bb-empty-panel"
            >
              No worn locations currently have stored or attached inventory.
            </div>

            <div
              v-else
              class="form-group"
            >
              <div class="form-floating">
                <v-select
                  :options="inventoryNodes"
                  :get-option-label="formatInventoryNodeLabel"
                  :dropdown-should-open="dropdownShouldOpen"
                  class="form-control contents-dialog-select inventory-dialog-select"
                  placeholder="search..."
                  id="inventory_item"
                >
                  <template #search="{ attributes, events }">
                    <input
                      class="vs__search"
                      v-bind="attributes"
                      v-on="events"
                    />
                  </template>

                  <template #option="item">
                    <div
                      class="contents-option"
                      :class="inventoryNodeClass(item)"
                    >
                      <span
                        :class="{
                          'inventory-node-parent-text': item?.isParentRow,
                          'inventory-node-child-text':
                            item?.type === 'item' && !item?.isParentRow,
                        }"
                        :style="inventoryNodeTextStyle(item)"
                      >
                        {{ formatInventoryNodeLabel(item) }}
                      </span>
                    </div>
                  </template>

                  <template #selected-option="item">
                    <span
                      class="contents-option-selected"
                      :class="inventoryNodeClass(item)"
                    >
                      <span
                        :class="{
                          'inventory-node-parent-text': item?.isParentRow,
                          'inventory-node-child-text':
                            item?.type === 'item' && !item?.isParentRow,
                        }"
                        :style="inventoryNodeTextStyle(item)"
                      >
                        {{ formatInventoryNodeLabel(item) }}
                      </span>
                    </span>
                  </template>
                </v-select>
                <label
                  class="bb-float-label"
                  for="inventory_item"
                  >Item</label
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PopUpContents>
</template>

<script>
import { inject } from 'vue'

import PopUpContents from './PopUpContents.vue'
import ToolTip from '../components/ToolTip.vue'

export default {
  name: 'InventoryDialog',
  components: {
    PopUpContents,
    ToolTip,
  },
  setup() {
    const global = inject('global')
    return { global }
  },
  data: () => ({
    character: {},
    character_items: [],
    inventoryNodes: [],
    wornLocations: [],
    resolvePromise: undefined,
    rejectPromise: undefined,
  }),
  computed: {
    totalEnc() {
      return this.calculateMaxEnc(this.character)
    },
    currentEnc() {
      const total = this.character_items.reduce((runningTotal, item) => {
        const encPerItem = this.toNumber(item?.enc)
        const qty = this.getItemQty(item)
        return runningTotal + encPerItem * qty
      }, 0)

      return this.roundToTwo(total)
    },
    availableEnc() {
      return this.roundToTwo(this.totalEnc - this.currentEnc)
    },
    directItemsCount() {
      return this.sumQty(
        this.character_items.filter((item) => this.isSet(item?.worn_location)),
      )
    },
    totalItemsCount() {
      return this.sumQty(this.character_items)
    },
    storedItemsCount() {
      return this.sumQty(
        this.character_items.filter((item) => this.isSet(item?.container)),
      )
    },
    attachedItemsCount() {
      return this.sumQty(
        this.character_items.filter((item) =>
          this.isSet(item?.attach_location),
        ),
      )
    },
    displayTotalEnc() {
      const str = this.character?.str_cur ?? 0
      const siz = this.character?.siz_cur ?? 0
      return `Max Enc (${str} + 2 x ${siz})`
    },
    displayCurrentEnc() {
      return 'Sum of all carried item encumbrance including tally quantities'
    },
    displayAvailableEnc() {
      return `Total Enc (${this.formatEnc(this.totalEnc)}) - Current Enc (${this.formatEnc(this.currentEnc)})`
    },
    displayDirectItems() {
      return 'Count of directly worn items across all worn locations'
    },
    displayTotalItems() {
      return 'Count of all inventory items including nested stored and attached items'
    },
    displayStoredItems() {
      return 'Count of items stored in containers'
    },
    displayAttachedItems() {
      return 'Count of items attached to other items'
    },
  },
  methods: {
    calculateMaxEnc(character) {
      const str = this.toNumber(character?.str_cur)
      const siz = this.toNumber(character?.siz_cur)
      return this.roundToTwo(str + 2 * siz)
    },
    show(opts = {}) {
      this.character = opts.character || {}
      this.character_items = Array.isArray(opts.character?.items)
        ? opts.character.items
        : []
      this.wornLocations = this.getWornLocations()
      this.inventoryNodes = this.buildInventoryNodes()

      this.$refs.popup.open()

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },
    dropdownShouldOpen() {
      return true
    },
    _cancel() {
      this.$refs.popup.close()
      if (this.resolvePromise) {
        this.resolvePromise()
      }
    },
    formatEnc(value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }

      const num = Number(value)
      if (Number.isNaN(num)) {
        return ''
      }

      if (this.global && typeof this.global.twoDecimal === 'function') {
        return this.global.twoDecimal(num)
      }

      return num.toFixed(2)
    },
    toNumber(value) {
      const num = Number(value)
      return Number.isNaN(num) ? 0 : num
    },
    roundToTwo(value) {
      return Math.round((this.toNumber(value) + Number.EPSILON) * 100) / 100
    },
    isTruthyFlag(value) {
      return value === true || value === 1 || value === '1' || value === 'true'
    },
    isSet(value) {
      return value !== null && value !== undefined && value !== ''
    },
    getLocationKey(value) {
      return this.isSet(value) ? String(value) : null
    },
    getItemQty(item) {
      const qty = parseInt(item?.tally_qty ?? 0, 10)
      return Number.isFinite(qty) && qty > 0 ? qty : 0
    },
    getItemOwnEnc(item) {
      return this.roundToTwo(this.toNumber(item?.enc) * this.getItemQty(item))
    },
    getManagedChildItems(parentId) {
      return this.sortItems([
        ...this.getDirectAttachedItems(parentId),
        ...this.getDirectStoredItems(parentId),
      ])
    },
    getInventoryRowEnc(item, ancestors = new Set()) {
      if (!item?.id || ancestors.has(item.id)) {
        return this.getItemOwnEnc(item)
      }

      const branchAncestors = new Set(ancestors)
      branchAncestors.add(item.id)

      const children = this.getManagedChildItems(item.id)
      const childEnc = children.reduce(
        (total, child) =>
          total + this.getInventoryRowEnc(child, branchAncestors),
        0,
      )

      return this.roundToTwo(this.getItemOwnEnc(item) + childEnc)
    },
    sumQty(items = []) {
      return items.reduce((total, item) => total + this.getItemQty(item), 0)
    },
    getItemName(item) {
      return (
        item?.display_name ||
        item?.name ||
        item?.item?.display_name ||
        item?.item?.name ||
        'Unnamed Item'
      )
    },
    getWornLocations() {
      const getRace = this.$store.getters['races/getRaceByName']
      const race =
        typeof getRace === 'function'
          ? getRace(this.character?.race_name)
          : null
      const raceLocations = Array.isArray(race?.worn_item_locations)
        ? race.worn_item_locations
        : []
      const locationMap = new Map(
        raceLocations.map((location) => [
          this.getLocationKey(location.id),
          location,
        ]),
      )

      this.character_items.forEach((item) => {
        const locationKey = this.getLocationKey(item?.worn_location)
        if (!this.isSet(item?.worn_location) || locationMap.has(locationKey)) {
          return
        }

        locationMap.set(locationKey, {
          id: item.worn_location,
          name: item.worn_location_name || `Location ${item.worn_location}`,
        })
      })

      return Array.from(locationMap.values())
    },
    getDirectWornItems(locationId) {
      const locationKey = this.getLocationKey(locationId)
      return this.character_items.filter(
        (item) => this.getLocationKey(item?.worn_location) === locationKey,
      )
    },
    getDirectStoredItems(parentId) {
      return this.character_items.filter((item) => item?.container === parentId)
    },
    getDirectAttachedItems(parentId) {
      return this.character_items.filter(
        (item) => item?.attach_location === parentId,
      )
    },
    sortItems(items = []) {
      return [...items].sort((left, right) => {
        const leftStorage = this.isTruthyFlag(left?.storage) ? 1 : 0
        const rightStorage = this.isTruthyFlag(right?.storage) ? 1 : 0

        if (leftStorage !== rightStorage) {
          return leftStorage - rightStorage
        }

        return this.getItemName(left).localeCompare(this.getItemName(right))
      })
    },
    collectDescendantsFromRoots(rootItems = []) {
      const descendants = []
      const visited = new Set(rootItems.map((item) => item?.id))
      const stack = rootItems.map((item) => item?.id).filter(Boolean)

      while (stack.length > 0) {
        const currentId = stack.pop()
        const children = this.sortItems([
          ...this.getDirectStoredItems(currentId),
          ...this.getDirectAttachedItems(currentId),
        ])

        children.forEach((child) => {
          if (!child?.id || visited.has(child.id)) {
            return
          }

          visited.add(child.id)
          descendants.push(child)
          stack.push(child.id)
        })
      }

      return descendants
    },
    buildSummarySuffix(directItems = [], totalItems = []) {
      const directCount = this.sumQty(directItems)
      const totalCount = this.sumQty(totalItems)

      if (directCount > 0 && directCount !== totalCount) {
        return `[${directCount} direct / ${totalCount} total]`
      }

      if (totalCount > 0) {
        return `[${totalCount} total]`
      }

      return ''
    },
    buildItemLabel(item) {
      const qty = this.getItemQty(item)
      const rowEnc = this.getInventoryRowEnc(item)
      let label = `${this.getItemName(item)} - Enc (${this.formatEnc(rowEnc)})`

      if (qty > 1) {
        label += ` x ${qty}`
      }

      const attachedItems = item?.id ? this.getDirectAttachedItems(item.id) : []
      const attachedCount = this.sumQty(attachedItems)

      if (this.isTruthyFlag(item?.attach_point) && attachedCount > 0) {
        label += ` [${attachedCount} attached]`
      }

      return label
    },
    formatInventoryNodeLabel(item) {
      if (!item) return ''

      const depth =
        typeof item.depth === 'number' && item.depth > 0 ? item.depth : 0
      const indent = depth > 0 ? '\u00A0'.repeat(depth * 5) : ''
      return `${indent}${item.label}`
    },
    inventoryNodeClass(item) {
      if (!item) {
        return 'contents-option-non-storage'
      }

      if (item.type === 'location') {
        return 'contents-option-storage inventory-node-location'
      }

      if (item.type === 'group') {
        return 'contents-option-non-storage inventory-node-group'
      }

      if (item.type === 'item') {
        const classes = [
          item.isParentRow
            ? 'contents-option-storage'
            : 'contents-option-non-storage',
          'inventory-node-item',
        ]

        if (item.isParentRow) {
          classes.push('inventory-node-parent')
        }

        return classes.join(' ')
      }

      return 'contents-option-storage'
    },
    inventoryNodeStyle(item) {
      if (!item) {
        return {}
      }

      if (item.type === 'location' || item.type === 'group') {
        return { fontWeight: 700 }
      }

      if (item.type === 'item' && item.isParentRow) {
        return {
          color: '#66d2e5',
        }
      }

      return {}
    },
    inventoryNodeTextStyle(item) {
      if (!item) {
        return ''
      }

      if (item.type === 'location' || item.type === 'group') {
        return 'font-weight: 700;'
      }

      if (item.type === 'item' && item.isParentRow) {
        return 'color: #66d2e5 !important;'
      }

      return ''
    },
    appendItemNode(
      nodes,
      item,
      depth,
      relation = 'worn',
      ancestors = new Set(),
    ) {
      if (!item?.id || ancestors.has(item.id)) {
        return
      }

      const branchAncestors = new Set(ancestors)
      branchAncestors.add(item.id)

      const attachedItems = this.sortItems(this.getDirectAttachedItems(item.id))
      const storedItems = this.sortItems(this.getDirectStoredItems(item.id))
      const storedTotalItems = [
        ...storedItems,
        ...this.collectDescendantsFromRoots(storedItems),
      ]
      const summarySuffix = this.buildSummarySuffix(
        storedItems,
        storedTotalItems,
      )
      const emptySuffix =
        this.isTruthyFlag(item.storage) && storedItems.length === 0
          ? '[empty]'
          : ''

      nodes.push({
        key: `item-${relation}-${item.id}-${depth}`,
        type: 'item',
        depth,
        label: [this.buildItemLabel(item), summarySuffix, emptySuffix]
          .filter(Boolean)
          .join(' '),
        storage: item.storage,
        attach_point: item.attach_point,
        hasAttachedChildren: attachedItems.length > 0,
        hasStoredChildren: storedItems.length > 0,
        isParentRow: attachedItems.length > 0 || storedItems.length > 0,
      })

      attachedItems.forEach((attachedItem) => {
        this.appendItemNode(
          nodes,
          attachedItem,
          depth + 1,
          'attached',
          branchAncestors,
        )
      })

      if (storedItems.length === 0) {
        return
      }

      storedItems.forEach((storedItem) => {
        this.appendItemNode(
          nodes,
          storedItem,
          depth + 1,
          'stored',
          branchAncestors,
        )
      })
    },
    locationHasManagedInventory(locationId) {
      const directItems = this.sortItems(this.getDirectWornItems(locationId))
      if (directItems.length === 0) {
        return false
      }

      return directItems.some((item) => {
        const directStored = this.getDirectStoredItems(item.id)
        const directAttached = this.getDirectAttachedItems(item.id)
        return directStored.length > 0 || directAttached.length > 0
      })
    },
    buildInventoryNodes() {
      const nodes = []

      this.wornLocations.forEach((location) => {
        if (!this.locationHasManagedInventory(location.id)) {
          return
        }

        const directItems = this.sortItems(this.getDirectWornItems(location.id))
        const totalItems = [
          ...directItems,
          ...this.collectDescendantsFromRoots(directItems),
        ]
        const locationEnc = directItems.reduce(
          (total, item) => total + this.getInventoryRowEnc(item),
          0,
        )
        const summarySuffix = this.buildSummarySuffix(directItems, totalItems)

        nodes.push({
          key: `location-${location.id}`,
          type: 'location',
          depth: 0,
          label: [
            `Location: ${location.name} - Enc (${this.formatEnc(locationEnc)})`,
            summarySuffix,
          ]
            .filter(Boolean)
            .join(' '),
        })

        directItems.forEach((item) => {
          this.appendItemNode(nodes, item, 1)
        })
      })

      return nodes
    },
  },
}
</script>

<style scoped>
:deep(.inventory-dialog-select) {
  text-transform: none;
}

:deep(.inventory-dialog-select .vs__dropdown-menu) {
  display: block;
  position: static;
  border: none;
  box-shadow: none;
}

:deep(.inventory-dialog-select .vs__selected-options) {
  max-height: none;
}

:deep(.contents-dialog-select .vs__dropdown-menu) {
  max-height: 36rem;
  overflow-y: auto;
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0.5rem;
}

:deep(.contents-dialog-select .vs__dropdown-menu),
:deep(.contents-dialog-select .vs__selected),
:deep(.contents-dialog-select .vs__search) {
  text-transform: none;
}

:deep(.contents-dialog-select .contents-option) {
  padding-top: 0.1rem;
  display: flex;
  align-items: center;
  font-size: 0.9em;
}

:deep(.contents-dialog-select .contents-option-storage),
:deep(
  .contents-dialog-select .contents-option-selected.contents-option-storage
) {
  color: #ffffff;
}

:deep(.contents-dialog-select .contents-option-non-storage),
:deep(
  .contents-dialog-select .contents-option-selected.contents-option-non-storage
) {
  color: #7d7c7c;
}

.bb-empty-panel {
  color: #7d7c7c;
  font-size: 0.9em;
  padding-top: 0.1rem;
}

:deep(.contents-dialog-select .inventory-node-location),
:deep(
  .contents-dialog-select .contents-option-selected.inventory-node-location
),
:deep(.contents-dialog-select .inventory-node-group),
:deep(.contents-dialog-select .contents-option-selected.inventory-node-group) {
  font-weight: 700;
}

:deep(.contents-dialog-select .inventory-node-parent),
:deep(.contents-dialog-select .contents-option-selected.inventory-node-parent) {
  color: #66d2e5;
}

:deep(.contents-dialog-select .inventory-node-parent-text) {
  color: #66d2e5 !important;
  -webkit-text-fill-color: #66d2e5 !important;
}

:deep(.contents-dialog-select .inventory-node-child-text) {
  color: #7d7c7c !important;
  -webkit-text-fill-color: #7d7c7c !important;
}

.bb-enc-overload {
  background-color: rgb(198, 4, 4) !important;
  color: #ffffff !important;
}
</style>
